import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Clock, 
  Target, 
  CheckCircle, 
  XCircle, 
  Trophy, 
  RefreshCw,
  Star,
  Award,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrishaGame = () => {
  const { token, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [gameState, setGameState] = useState('menu'); // menu, playing, completed
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [timeLeft, setTimeLeft] = useState(30);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [maxWrongAnswers, setMaxWrongAnswers] = useState(5);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [gameResults, setGameResults] = useState(null);
  const [difficulty, setDifficulty] = useState('beginner');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [showTopicSelection, setShowTopicSelection] = useState(false);
  const [showGameModeSelection, setShowGameModeSelection] = useState(false);
  const [selectedGameMode, setSelectedGameMode] = useState('multiple_choice');
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [currentQuestionData, setCurrentQuestionData] = useState(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState(null);

  const difficulties = [
    {
      value: 'beginner',
      label: 'Principiante',
      description: 'Aprende los conceptos básicos de los Orishas',
      icon: '🌱',
      color: 'from-green-500 to-emerald-600'
    },
    {
      value: 'intermediate',
      label: 'Intermedio',
      description: 'Profundiza en el conocimiento de plantas y rituales',
      icon: '🌿',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      value: 'advanced',
      label: 'Avanzado',
      description: 'Domina símbolos, atributos y correspondencias',
      icon: '🔮',
      color: 'from-purple-500 to-violet-600'
    },
    {
      value: 'expert',
      label: 'Experto',
      description: 'Conocimiento completo de todas las tradiciones',
      icon: '👑',
      color: 'from-amber-500 to-orange-600'
    }
  ];

  const availableTopics = [
    { id: 'colors', name: 'Colores', description: 'Los colores sagrados de cada Orisha', icon: '🎨', difficulty: 'easy' },
    { id: 'plants', name: 'Plantas', description: 'Hierbas y plantas asociadas', icon: '🌿', difficulty: 'medium' },
    { id: 'animals', name: 'Animales', description: 'Animales sagrados y ofrendas', icon: '🐾', difficulty: 'easy' },
    { id: 'days', name: 'Días', description: 'Días de celebración y ritual', icon: '📅', difficulty: 'medium' },
    { id: 'symbols', name: 'Ofrendas', description: 'Ofrendas y Adimuses para las Deidades', icon: '🍯', difficulty: 'hard' },
    { id: 'attributes', name: 'Deidades', description: 'Deidades y poderes Yoruba', icon: '👑', difficulty: 'hard' },
    { id: 'random', name: 'Aleatorio', description: 'Combinación aleatoria (mayor dificultad)', icon: '🎲', difficulty: 'expert' }
  ];

  const gameModes = [
    { 
      id: 'multiple_choice', 
      name: 'Opción Múltiple', 
      description: 'Elige la respuesta correcta entre 4 opciones', 
      icon: '📝' 
    },
    { 
      id: 'image_match', 
      name: 'Imagen vs Nombre', 
      description: 'Relaciona imágenes con nombres de Orishas', 
      icon: '🖼️' 
    },
    { 
      id: 'type_answer', 
      name: 'Escribir Respuesta', 
      description: 'Escribe el nombre del Orisha directamente', 
      icon: '⌨️' 
    },
    { 
      id: 'audio_visual', 
      name: 'Audio-Visual', 
      description: 'Identifica basándote en sonidos y colores', 
      icon: '🎵' 
    }
  ];

  // Timer effect
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0 && !selectedAnswer) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !selectedAnswer) {
      handleAnswerSubmit(''); // Auto submit empty answer when time runs out
    }
  }, [gameState, timeLeft, selectedAnswer]);

  const startGame = async (selectedDifficulty, topics = [], gameMode = 'multiple_choice') => {
    setLoading(true);
    try {
      const topicsParam = topics.length > 0 ? `&topics=${topics.join(',')}` : '';
      const response = await axios.post(`${API}/games/orisha-matching/start?difficulty=${selectedDifficulty}${topicsParam}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Set time limit based on difficulty
      let timeLimit;
      let maxWrong;
      switch (selectedDifficulty) {
        case 'beginner':
          timeLimit = 30;
          maxWrong = 5;
          break;
        case 'intermediate':
          timeLimit = 20;
          maxWrong = 3;
          break;
        case 'advanced':
          timeLimit = 15;
          maxWrong = 2;
          break;
        case 'expert':
          timeLimit = 10;
          maxWrong = 1;
          break;
        default:
          timeLimit = 30;
          maxWrong = 5;
      }
      
      setSessionId(response.data.session_id);
      setQuestions(response.data.questions);
      setDifficulty(selectedDifficulty);
      setGameState('playing');
      setCurrentQuestionIndex(0);
      setScore({ correct: 0, total: 0 });
      setTimeLeft(timeLimit);
      setMaxWrongAnswers(maxWrong);
      setWrongAnswers(0);
      setIsDisqualified(false);
      setSelectedAnswer('');
      setShowResult(false);
      
      toast.success('¡Juego iniciado! ¡Buena suerte!');
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Error al iniciar el juego');
    } finally {
      setLoading(false);
    }
  };

  const handleDifficultySelect = (selectedDifficulty) => {
    setDifficulty(selectedDifficulty);
    setShowTopicSelection(true);
  };

  const handleTopicSelection = () => {
    if (selectedTopics.length === 0) {
      toast.error('Selecciona al menos un tema para estudiar');
      return;
    }
    
    setShowGameModeSelection(true);
  };

  const handleGameModeSelection = () => {
    startGame(difficulty, selectedTopics, selectedGameMode);
  };

  const handleAnswerSelect = (answer) => {
    if (!selectedAnswer && timeLeft > 0) {
      setSelectedAnswer(answer);
      setTimeout(() => handleAnswerSubmit(answer), 500);
    }
  };

  const handleAnswerSubmit = async (answer, answers = null) => {
    if (!sessionId || !questions[currentQuestionIndex]) return;
    
    try {
      const submitData = {
        question_id: questions[currentQuestionIndex].id
      };
      
      if (answers && answers.length > 0) {
        // Multiple answers
        submitData.answers = answers;
        submitData.answer = answers[0]; // Fallback for compatibility
      } else {
        // Single answer
        submitData.answer = answer;
      }
      
      const response = await axios.post(`${API}/games/${sessionId}/answer`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const isCorrect = response.data.is_correct;
      setLastAnswerCorrect(isCorrect);
      setCurrentQuestionData(response.data); // Store full response data
      setShowResult(true);
      
      // Update score
      if (isCorrect) {
        setScore(prev => ({ ...prev, correct: prev.correct + 1, total: prev.total + 1 }));
      } else {
        setScore(prev => ({ ...prev, total: prev.total + 1 }));
        
        // Update strikes system for wrong answers
        if (response.data.wrong_answers !== undefined) {
          setWrongAnswers(response.data.wrong_answers);
        }
        
        // Check for disqualification
        if (response.data.disqualified) {
          setIsDisqualified(true);
          toast.error(response.data.message || 'Has sido descalificado');
          setTimeout(() => {
            completeGame();
          }, 3000);
          return;
        } else if (response.data.remaining_strikes !== undefined) {
          toast.warning(`${response.data.remaining_strikes} oportunidades restantes`, {
            duration: 4000,
            icon: '⚠️'
          });
        }
      }

      // For correct answers, auto-advance after 2 seconds
      // For incorrect answers, show continue button after 3 seconds to give time to read
      if (isCorrect) {
        const timer = setTimeout(() => {
          moveToNextQuestion();
        }, 2000);
        setAutoAdvanceTimer(timer);
      } else {
        // For wrong answers, show continue button after 3 seconds of reading time (if not disqualified)
        if (!response.data.disqualified) {
          setTimeout(() => {
            setShowContinueButton(true);
          }, 3000);
        }
      }
      
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Error al enviar respuesta');
    }
  };

  const moveToNextQuestion = () => {
    // Clear any existing timer
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      setAutoAdvanceTimer(null);
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      // Get correct time limit based on difficulty
      let timeLimit;
      switch (difficulty) {
        case 'beginner':
          timeLimit = 30;
          break;
        case 'intermediate':
          timeLimit = 20;
          break;
        case 'advanced':
          timeLimit = 15;
          break;
        case 'expert':
          timeLimit = 10;
          break;
        default:
          timeLimit = 30;
      }
      
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer('');
      setSelectedAnswers([]);
      setTypedAnswer('');
      setTimeLeft(timeLimit);
      setShowResult(false);
      setLastAnswerCorrect(null);
      setCurrentQuestionData(null);
      setShowContinueButton(false);
    } else {
      completeGame();
    }
  };

  const handleContinue = () => {
    moveToNextQuestion();
  };

  const completeGame = async () => {
    try {
      const response = await axios.get(`${API}/games/${sessionId}/complete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGameResults(response.data);
      setGameState('completed');
      
      // Refresh user data to show updated XP and level
      await refreshUser();
      
      if (response.data.percentage >= 80) {
        toast.success('¡Excelente trabajo!');
      } else if (response.data.percentage >= 60) {
        toast.success('¡Buen trabajo!');
      } else {
        toast.success('¡Sigue practicando!');
      }
      
      // Show XP earned notification
      if (response.data.xp_earned && response.data.xp_earned > 0) {
        setTimeout(() => {
          toast.success(`¡Has ganado ${response.data.xp_earned} XP!`, {
            duration: 4000,
            icon: '⭐'
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Error completing game:', error);
      toast.error('Error al completar el juego');
    }
  };

  const resetGame = () => {
    // Clear any existing timer
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      setAutoAdvanceTimer(null);
    }
    
    // Reset game state but keep same settings
    setSessionId(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setScore({ correct: 0, total: 0 });
    setTimeLeft(30);
    setSelectedAnswer('');
    setGameResults(null);
    setShowResult(false);
    setLastAnswerCorrect(null);
    setSelectedAnswers([]);
    setTypedAnswer('');
    setWrongAnswers(0);
    setIsDisqualified(false);
    
    // Restart game with same settings instead of going to menu
    startGame(difficulty, selectedTopics, selectedGameMode);
    setCurrentQuestionData(null);
    setSelectedTopics([]);
    setShowTopicSelection(false);
    setShowGameModeSelection(false);
    setSelectedGameMode('multiple_choice');
    setShowContinueButton(false);
    setWrongAnswers(0);
    setMaxWrongAnswers(5);
    setIsDisqualified(false);
    setSelectedAnswers([]);
    setTypedAnswer('');
  };

  const currentQuestion = questions[currentQuestionIndex];

  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/dashboard"
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Conocimiento de Orishas</h1>
              <p className="text-emerald-600">Elige tu nivel de dificultad y los temas que deseas estudiar</p>
            </div>
          </div>

          {!showTopicSelection ? (
            /* Difficulty Selection */
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Selecciona tu Nivel</h2>
                <p className="text-emerald-700 max-w-2xl mx-auto">
                  Cada nivel está diseñado para diferentes etapas de aprendizaje. 
                  Comienza con Principiante si es tu primera vez.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {difficulties.map((diff, index) => (
                  <div 
                    key={index} 
                    className={`game-card bg-gradient-to-br ${diff.color} p-8 rounded-2xl text-white cursor-pointer relative overflow-hidden`}
                    onClick={() => handleDifficultySelect(diff.value)}
                  >
                    <div className="text-6xl mb-4 text-center">{diff.icon}</div>
                    <h3 className="font-cinzel text-2xl font-bold mb-3 text-center">{diff.label}</h3>
                    <p className="text-white text-opacity-90 text-center leading-relaxed mb-6">
                      {diff.description}
                    </p>
                    
                    <div className="text-center">
                      <button 
                        className="bg-white text-emerald-800 px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
                        disabled={loading}
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-800"></div>
                        ) : (
                          <>
                            <Target className="w-5 h-5" />
                            Seleccionar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !showGameModeSelection ? (
            /* Topic Selection */
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <button 
                  onClick={() => setShowTopicSelection(false)}
                  className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-800 mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cambiar dificultad
                </button>
                <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">
                  Selecciona los Temas a Estudiar
                </h2>
                <p className="text-emerald-700 max-w-2xl mx-auto">
                  Nivel: <span className="font-bold capitalize">{difficulty}</span>. 
                  Elige uno o varios temas para personalizar tu aprendizaje.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {availableTopics.map((topic, index) => (
                  <div 
                    key={index}
                    className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-300 ${
                      selectedTopics.includes(topic.id)
                        ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105'
                        : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                    }`}
                    onClick={() => {
                      if (topic.id === 'random') {
                        setSelectedTopics(['random']);
                      } else {
                        setSelectedTopics(prev => 
                          prev.includes(topic.id)
                            ? prev.filter(t => t !== topic.id && t !== 'random')
                            : [...prev.filter(t => t !== 'random'), topic.id]
                        );
                      }
                    }}
                  >
                    <div className="text-4xl mb-3 text-center">{topic.icon}</div>
                    <h3 className="font-bold text-lg text-emerald-800 text-center mb-2">{topic.name}</h3>
                    <p className="text-emerald-600 text-sm text-center mb-3">{topic.description}</p>
                    <div className={`text-xs px-2 py-1 rounded-full text-center ${
                      topic.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                      topic.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      topic.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {topic.difficulty === 'easy' ? 'Fácil' : 
                       topic.difficulty === 'medium' ? 'Medio' : 
                       topic.difficulty === 'hard' ? 'Difícil' : 'Experto'}
                    </div>
                    
                    {selectedTopics.includes(topic.id) && (
                      <div className="mt-3 text-center">
                        <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="text-center">
                <div className="mb-4 text-emerald-700">
                  <strong>Temas seleccionados:</strong> {
                    selectedTopics.length === 0 ? 'Ninguno' :
                    selectedTopics.includes('random') ? 'Aleatorio (Todos los temas)' :
                    selectedTopics.map(id => availableTopics.find(t => t.id === id)?.name).join(', ')
                  }
                </div>
                
                <button
                  onClick={handleTopicSelection}
                  disabled={selectedTopics.length === 0 || loading}
                  className="bg-emerald-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Star className="w-6 h-6" />
                      Continuar
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Game Mode Selection */
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <button 
                  onClick={() => setShowGameModeSelection(false)}
                  className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-800 mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cambiar temas
                </button>
                <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">
                  Selecciona el Modo de Juego
                </h2>
                <p className="text-emerald-700 max-w-2xl mx-auto">
                  Dificultad: <span className="font-bold capitalize">{difficulty}</span> • 
                  Temas: <span className="font-bold">
                    {selectedTopics.includes('random') ? 'Aleatorio' : 
                     selectedTopics.map(id => availableTopics.find(t => t.id === id)?.name).join(', ')}
                  </span>
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {gameModes.map((mode, index) => (
                  <div 
                    key={index}
                    className={`cursor-pointer p-8 rounded-xl border-2 transition-all duration-300 ${
                      selectedGameMode === mode.id
                        ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105'
                        : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                    }`}
                    onClick={() => setSelectedGameMode(mode.id)}
                  >
                    <div className="text-6xl mb-4 text-center">{mode.icon}</div>
                    <h3 className="font-bold text-xl text-emerald-800 text-center mb-3">{mode.name}</h3>
                    <p className="text-emerald-600 text-center leading-relaxed">{mode.description}</p>
                    
                    {selectedGameMode === mode.id && (
                      <div className="mt-4 text-center">
                        <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="text-center">
                <div className="mb-4 text-emerald-700">
                  <strong>Modo seleccionado:</strong> {gameModes.find(m => m.id === selectedGameMode)?.name}
                </div>
                
                <button
                  onClick={handleGameModeSelection}
                  disabled={loading}
                  className="bg-emerald-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Star className="w-6 h-6" />
                      Comenzar Juego
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Game Playing Screen
  if (gameState === 'playing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700">
        <div className="container mx-auto px-6 py-8">
          {/* Game Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={resetGame}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Conocimiento de Orishas</h1>
                <p className="text-emerald-200 capitalize">{difficulty}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{score.correct}/{score.total}</div>
                <div className="text-emerald-200 text-sm">Correctas</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  wrongAnswers >= maxWrongAnswers - 1 ? 'text-red-400' : 
                  wrongAnswers >= Math.ceil(maxWrongAnswers / 2) ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  ❌ {wrongAnswers}/{maxWrongAnswers}
                </div>
                <div className="text-emerald-200 text-sm">Errores</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold ${timeLeft <= 10 ? 'text-red-400 animate-pulse' : ''}`}>
                  {timeLeft}
                </div>
                <div className="text-emerald-200 text-sm">Segundos</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-emerald-200 text-sm mb-2">
              <span>Pregunta {currentQuestionIndex + 1} de {questions.length}</span>
              <span>{Math.round(((currentQuestionIndex) / questions.length) * 100)}% Completado</span>
            </div>
            <div className="progress-bar bg-emerald-800">
              <div 
                className="progress-fill bg-gradient-to-r from-yellow-400 to-yellow-500" 
                style={{ width: `${((currentQuestionIndex) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          {currentQuestion && (
            <div className="max-w-4xl mx-auto">
              {/* Question Content - Multiple Choice Mode */}
              {selectedGameMode === 'multiple_choice' && (
                <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                  <h2 className="font-cinzel text-2xl font-bold text-emerald-800 text-center mb-8">
                    {currentQuestion.question}
                  </h2>
                  
                  {currentQuestion.is_multiple_answer ? (
                    /* Multiple Answer Selection */
                    <div>
                      <div className="mb-4 text-center text-sm text-emerald-600 bg-emerald-50 p-2 rounded-lg">
                        💡 Esta pregunta puede tener múltiples respuestas correctas
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {currentQuestion.options.map((option, index) => (
                          <label
                            key={index}
                            className={`flex items-center p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                              selectedAnswers.includes(option)
                                ? 'bg-emerald-100 border-emerald-500'
                                : 'bg-gray-50 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                            } ${showResult ? 'cursor-not-allowed' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAnswers.includes(option)}
                              onChange={(e) => {
                                if (showResult) return;
                                if (e.target.checked) {
                                  setSelectedAnswers(prev => [...prev, option]);
                                } else {
                                  setSelectedAnswers(prev => prev.filter(ans => ans !== option));
                                }
                              }}
                              disabled={showResult}
                              className="mr-3 w-4 h-4 text-emerald-600"
                            />
                            <span className="font-semibold text-emerald-800">{option}</span>
                          </label>
                        ))}
                      </div>
                      <div className="text-center mt-6">
                        <button
                          onClick={() => handleAnswerSubmit(null, selectedAnswers)}
                          disabled={showResult || selectedAnswers.length === 0}
                          className="bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Confirmar Respuestas ({selectedAnswers.length})
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Single Answer Selection */
                    <div className="grid md:grid-cols-2 gap-4">
                      {currentQuestion.options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => handleAnswerSelect(option)}
                          disabled={selectedAnswer || timeLeft === 0}
                          className={`p-6 rounded-xl text-left font-semibold transition-all duration-300 border-2 ${
                            selectedAnswer === option
                              ? showResult
                                ? lastAnswerCorrect && (currentQuestion.correct_answers || [currentQuestion.correct_answer]).includes(selectedAnswer)
                                  ? 'border-green-500 bg-green-50 text-green-800'
                                  : 'border-red-500 bg-red-50 text-red-800'
                                : 'border-emerald-500 bg-emerald-50 text-emerald-800'
                              : showResult && (currentQuestion.correct_answers || [currentQuestion.correct_answer]).includes(option)
                              ? 'border-green-500 bg-green-50 text-green-800'
                              : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
                          } ${selectedAnswer || timeLeft === 0 ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                              selectedAnswer === option
                                ? showResult
                                  ? lastAnswerCorrect && (currentQuestion.correct_answers || [currentQuestion.correct_answer]).includes(selectedAnswer)
                                    ? 'border-green-500 bg-green-500 text-white'
                                    : 'border-red-500 bg-red-500 text-white'
                                  : 'border-emerald-500 bg-emerald-500 text-white'
                                : showResult && (currentQuestion.correct_answers || [currentQuestion.correct_answer]).includes(option)
                                ? 'border-green-500 bg-green-500 text-white'
                                : 'border-gray-400 text-gray-600'
                            }`}>
                              {String.fromCharCode(65 + index)}
                            </div>
                            <span>{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Type Answer Mode */}
              {selectedGameMode === 'type_answer' && (
                <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                  <h2 className="font-cinzel text-2xl font-bold text-emerald-800 text-center mb-8">
                    {currentQuestion.question}
                  </h2>
                  
                  <div className="max-w-md mx-auto">
                    <input
                      type="text"
                      value={typedAnswer}
                      onChange={(e) => setTypedAnswer(e.target.value)}
                      disabled={showResult}
                      placeholder="Escribe tu respuesta aquí..."
                      className="w-full px-4 py-3 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-center text-lg font-semibold"
                    />
                    
                    <div className="flex gap-3 mt-6 justify-center">
                      <button
                        onClick={() => handleAnswerSubmit(typedAnswer.trim())}
                        disabled={showResult || !typedAnswer.trim()}
                        className="bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Confirmar Respuesta
                      </button>
                      
                      <button
                        onClick={() => {/* TODO: Show hint */}}
                        disabled={showResult}
                        className="bg-yellow-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50"
                      >
                        💡 Pista (-5 XP)
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Image vs Names Mode - Coming Soon */}
              {selectedGameMode === 'image_match' && (
                <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🚧</div>
                    <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">
                      Modo Imagen vs Nombres
                    </h2>
                    <p className="text-emerald-600 mb-6">
                      Este modo estará disponible próximamente con imágenes interactivas de los Orishas
                    </p>
                    <button
                      onClick={() => setSelectedGameMode('multiple_choice')}
                      className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors"
                    >
                      Cambiar a Opción Múltiple
                    </button>
                  </div>
                </div>
              )}

              {/* Audio Visual Mode - Coming Soon */}
              {selectedGameMode === 'audio_visual' && (
                <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🚧</div>
                    <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">
                      Modo Audio-Visual
                    </h2>
                    <p className="text-emerald-600 mb-6">
                      Este modo estará disponible próximamente con experiencias audiovisuales inmersivas
                    </p>
                    <button
                      onClick={() => setSelectedGameMode('multiple_choice')}
                      className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors"
                    >
                      Cambiar a Opción Múltiple
                    </button>
                  </div>
                </div>
              )}

              {/* Result Feedback */}
              {showResult && (
                <div className={`text-center p-6 rounded-2xl ${
                  lastAnswerCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    {lastAnswerCorrect ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-600" />
                    )}
                    <span className="text-xl font-bold">
                      {lastAnswerCorrect ? '¡Correcto! ¡Excelente conocimiento!' : 'Respuesta Incorrecta'}
                    </span>
                  </div>
                  
                  {!lastAnswerCorrect && (
                    <div className="space-y-4">
                      <div className="bg-white bg-opacity-50 p-4 rounded-xl">
                        <div className="mb-3">
                          <p className="text-lg mb-2">
                            <strong>Respuesta(s) correcta(s):</strong> {
                              currentQuestionData?.correct_answers?.join(', ') || currentQuestionData?.correct_answer
                            }
                          </p>
                          <p className="text-sm text-gray-700">
                            <strong>Tu respuesta:</strong> {
                              currentQuestionData?.submitted_answers?.length > 0 
                                ? currentQuestionData.submitted_answers.join(', ')
                                : selectedAnswer || 'No seleccionada'
                            }
                          </p>
                        </div>
                        
                        {/* Show detailed feedback for multiple answers */}
                        {currentQuestionData?.is_multiple_answer && (
                          <div className="mt-3 space-y-2">
                            {currentQuestionData?.correctly_selected?.length > 0 && (
                              <div className="text-sm">
                                ✅ <strong>Correctas seleccionadas:</strong> {currentQuestionData.correctly_selected.join(', ')}
                              </div>
                            )}
                            {currentQuestionData?.incorrectly_selected?.length > 0 && (
                              <div className="text-sm">
                                ❌ <strong>Incorrectas seleccionadas:</strong> {currentQuestionData.incorrectly_selected.join(', ')}
                              </div>
                            )}
                            {currentQuestionData?.missed_answers?.length > 0 && (
                              <div className="text-sm">
                                ⚠️ <strong>Te faltaron:</strong> {currentQuestionData.missed_answers.join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {currentQuestionData?.learning_reference && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl">
                          <div className="flex items-start gap-3">
                            <div className="text-2xl">📚</div>
                            <div>
                              <h4 className="font-bold text-blue-800 mb-2">Aprende más:</h4>
                              <p className="text-blue-700 font-medium">
                                {currentQuestionData.learning_reference}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-xl">
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">💡</div>
                          <div>
                            <h4 className="font-bold text-yellow-800 mb-2">Consejo de estudio:</h4>
                            <p className="text-yellow-700 text-sm">
                              Tómate un momento para reflexionar sobre esta información. 
                              Los errores son oportunidades valiosas de aprendizaje en el camino espiritual.
                            </p>
                          </div>
                        </div>
                      </div>

                      {showContinueButton && (
                        <button
                          onClick={handleContinue}
                          className="bg-emerald-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-emerald-600 transition-colors flex items-center gap-2 mx-auto mt-4"
                        >
                          <CheckCircle className="w-5 h-5" />
                          He leído y entendido - Continuar
                        </button>
                      )}
                      
                      {!showContinueButton && (
                        <div className="flex items-center justify-center gap-2 text-gray-600 text-sm mt-4">
                          <Clock className="w-4 h-4" />
                          <span>Tómate tiempo para leer y comprender...</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {lastAnswerCorrect && (
                    <div className="mt-4">
                      <div className="flex items-center justify-center gap-2 text-green-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        <span>Continuando automáticamente...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Game Completed Screen
  if (gameState === 'completed' && gameResults) {
    // Check if failed (under 50%)
    if (!gameResults.passed) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
              <div className="mb-8">
                <div className="text-6xl mb-4">😔</div>
                <h1 className="font-cinzel text-3xl font-bold text-red-800 mb-4">¡No te rindas!</h1>
                <p className="text-lg font-semibold text-red-600">
                  Necesitas al menos 50% para aprobar. ¡La práctica te llevará al éxito!
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="bg-red-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Target className="w-8 h-8 text-red-600" />
                  </div>
                  <div className="text-2xl font-bold text-red-800">{gameResults.percentage}%</div>
                  <div className="text-sm text-gray-600">Tu Puntuación</div>
                </div>
                
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-2xl font-bold text-red-800">{gameResults.correct_answers}</div>
                  <div className="text-sm text-gray-600">Correctas</div>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-red-800">50%</div>
                  <div className="text-sm text-gray-600">Mínimo Requerido</div>
                </div>
              </div>

              <div className="flex flex-col gap-4 justify-center mb-8">
                <button
                  onClick={resetGame}
                  className="bg-emerald-500 text-white px-12 py-4 rounded-xl font-bold text-lg hover:bg-emerald-600 transition-colors flex items-center justify-center gap-3 mx-auto shadow-lg"
                >
                  <RefreshCw className="w-6 h-6" />
                  Intentar de Nuevo
                </button>
                
                <Link
                  to="/dashboard"
                  className="bg-gray-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors flex items-center justify-center gap-2 mx-auto"
                >
                  <TrendingUp className="w-5 h-5" />
                  Volver al Dashboard
                </Link>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-blue-700 text-sm">
                  <strong>Consejo:</strong> Estudia las referencias de Oddun que aparecen cuando respondes incorrectamente. 
                  Cada error es una oportunidad de aprender más sobre los Orishas.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Regular success screen for 50% and above
    const getScoreMessage = (percentage) => {
      if (percentage >= 90) return { text: '¡Excelente trabajo!', icon: '🏆', color: 'text-yellow-600' };
      if (percentage >= 80) return { text: '¡Muy buen trabajo!', icon: '⭐', color: 'text-purple-600' };
      if (percentage >= 70) return { text: '¡Buen trabajo!', icon: '👍', color: 'text-blue-600' };
      if (percentage >= 60) return { text: '¡Sigue practicando!', icon: '💪', color: 'text-emerald-600' };
      return { text: '¡Aprobaste! Sigue mejorando', icon: '✅', color: 'text-green-600' };
    };

    const scoreMessage = getScoreMessage(gameResults.percentage);

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
            {/* Results Header */}
            <div className="mb-8">
              <div className="text-6xl mb-4">{scoreMessage.icon}</div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800 mb-4">¡Juego Completado!</h1>
              <p className={`text-lg font-semibold ${scoreMessage.color}`}>{scoreMessage.text}</p>
            </div>

            {/* Score Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="text-center">
                <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-8 h-8 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.percentage}%</div>
                <div className="text-sm text-gray-600">Puntuación</div>
              </div>
              
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.correct_answers}</div>
                <div className="text-sm text-gray-600">Correctas</div>
              </div>
              
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">{gameResults.total_questions}</div>
                <div className="text-sm text-gray-600">Preguntas</div>
              </div>
              
              <div className="text-center">
                <div className="bg-yellow-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Star className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="text-2xl font-bold text-emerald-800">+{gameResults.xp_earned || 0}</div>
                <div className="text-sm text-gray-600">XP Ganados</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={resetGame}
                className="btn-primary px-6 py-3 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Jugar de Nuevo
              </button>
              
              <Link
                to="/dashboard"
                className="btn-gold px-6 py-3 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <TrendingUp className="w-5 h-5" />
                Ver Progreso
              </Link>
            </div>

            {/* Progress Message */}
            <div className="mt-8 p-4 bg-emerald-50 rounded-xl">
              <p className="text-emerald-700 text-sm">
                {gameResults.percentage >= 80 
                  ? '¡Felicidades! Has demostrado un excelente conocimiento de los Orishas.'
                  : 'Continúa practicando para mejorar tu comprensión de las tradiciones Yoruba.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OrishaGame;